import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { runNavigationGuard, hasNavigationGuard } from '../utils/navigationGuard';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LayoutDashboard,
  Users,
  Store,
  ShoppingCart,
  Fuel,
  Receipt,
  CreditCard,
  FileText,
  Settings,
  ClipboardList,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Package,
  Ticket,
  DollarSign,
  TrendingUp,
  Truck,
  Landmark,
  Database,
  Clock,
  AlertTriangle,
  Coins,
  Sun,
  Moon,
  RefreshCw,
  Building2,
  Wallet,
  Gift,
  FolderOpen,
  Banknote,
  PiggyBank,
  Wrench
} from 'lucide-react';

// Grouped menu structure with submenus
const menuStructure = {
  super_user: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { 
      icon: ShoppingCart, 
      label: 'Daily Operations', 
      isGroup: true,
      children: [
        { icon: ShoppingCart, label: 'Sales Entry', path: '/sales-entry' },
        { icon: Receipt, label: 'Invoice Entry', path: '/purchases' },
        { icon: TrendingUp, label: 'Expenses', path: '/expenses' },
        { icon: Clock, label: 'Staff Hours', path: '/staff-hours' },
      ]
    },
    { 
      icon: Fuel, 
      label: 'Fuel', 
      isGroup: true,
      fuelOnly: true,
      children: [
        { icon: Fuel, label: 'Fuel Management', path: '/fuel' },
        { icon: CreditCard, label: 'Card Balances', path: '/fuel-card-balances' },
        { icon: AlertTriangle, label: 'Drive Off / Debt', path: '/drive-off-debt' },
        { icon: DollarSign, label: 'Retail Prices', path: '/fuel-prices' },
      ]
    },
    { 
      icon: Coins, 
      label: 'Other Revenue', 
      isGroup: true,
      children: [
        { icon: Package, label: 'Scratch Cards', path: '/scratch-cards' },
        { icon: Ticket, label: 'Lottery', path: '/lottery' },
        { icon: Coins, label: 'Commission Income', path: '/commission-income' },
      ]
    },
    { 
      icon: Banknote, 
      label: 'Finance', 
      isGroup: true,
      children: [
        { icon: DollarSign, label: 'Cash Control', path: '/money-movement' },
        { icon: Wallet, label: 'Credit Cards', path: '/credit-cards' },
        { icon: Building2, label: 'Bank Statements', path: '/bank-statements' },
        { icon: Landmark, label: 'Creditors', path: '/creditors' },
        { icon: PiggyBank, label: 'Business Loans', path: '/loans' },
        { icon: Gift, label: 'Vouchers', path: '/vouchers' },
      ]
    },
    { icon: RefreshCw, label: 'Reconciliation', path: '/reconciliation' },
    { icon: TrendingUp, label: 'Reports', path: '/reports' },
    { 
      icon: Wrench, 
      label: 'Admin', 
      isGroup: true,
      children: [
        { icon: Truck, label: 'Suppliers', path: '/suppliers' },
        { icon: Users, label: 'User Management', path: '/users' },
        { icon: Store, label: 'Store Management', path: '/stores' },
        { icon: FileText, label: 'Till Mapping', path: '/till-mapping' },
        { icon: ClipboardList, label: 'Approval Requests', path: '/approval-requests' },
        { icon: ClipboardList, label: 'Audit Log', path: '/audit' },
        { icon: Database, label: 'Backup & Restore', path: '/backup' },
        { icon: Settings, label: 'Settings', path: '/settings' },
      ]
    },
  ],
  admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { 
      icon: ShoppingCart, 
      label: 'Daily Operations', 
      isGroup: true,
      children: [
        { icon: ShoppingCart, label: 'Sales Entry', path: '/sales-entry' },
        { icon: Receipt, label: 'Invoice Entry', path: '/purchases' },
        { icon: TrendingUp, label: 'Expenses', path: '/expenses' },
        { icon: Clock, label: 'Staff Hours', path: '/staff-hours' },
      ]
    },
    { 
      icon: Fuel, 
      label: 'Fuel', 
      isGroup: true,
      fuelOnly: true,
      children: [
        { icon: Fuel, label: 'Fuel Management', path: '/fuel' },
        { icon: CreditCard, label: 'Card Balances', path: '/fuel-card-balances' },
        { icon: AlertTriangle, label: 'Drive Off / Debt', path: '/drive-off-debt' },
        { icon: DollarSign, label: 'Retail Prices', path: '/fuel-prices' },
      ]
    },
    { 
      icon: Coins, 
      label: 'Other Revenue', 
      isGroup: true,
      children: [
        { icon: Package, label: 'Scratch Cards', path: '/scratch-cards' },
        { icon: Ticket, label: 'Lottery', path: '/lottery' },
        { icon: Coins, label: 'Commission Income', path: '/commission-income' },
      ]
    },
    { 
      icon: Banknote, 
      label: 'Finance', 
      isGroup: true,
      children: [
        { icon: DollarSign, label: 'Cash Control', path: '/money-movement' },
        { icon: Wallet, label: 'Credit Cards', path: '/credit-cards' },
        { icon: Building2, label: 'Bank Statements', path: '/bank-statements' },
        { icon: Landmark, label: 'Creditors', path: '/creditors' },
        { icon: PiggyBank, label: 'Business Loans', path: '/loans' },
        { icon: Gift, label: 'Vouchers', path: '/vouchers' },
      ]
    },
    { icon: RefreshCw, label: 'Reconciliation', path: '/reconciliation' },
    { icon: TrendingUp, label: 'Reports', path: '/reports' },
    { 
      icon: Wrench, 
      label: 'Admin', 
      isGroup: true,
      children: [
        { icon: Truck, label: 'Suppliers', path: '/suppliers' },
        { icon: Users, label: 'User Management', path: '/users' },
        { icon: Store, label: 'Store Management', path: '/stores' },
        { icon: FileText, label: 'Till Mapping', path: '/till-mapping' },
        { icon: ClipboardList, label: 'Approval Requests', path: '/approval-requests' },
        { icon: ClipboardList, label: 'Audit Log', path: '/audit' },
        { icon: Database, label: 'Backup & Restore', path: '/backup' },
        { icon: Settings, label: 'Settings', path: '/settings' },
      ]
    },
  ],
  manager: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { 
      icon: ShoppingCart, 
      label: 'Daily Operations', 
      isGroup: true,
      children: [
        { icon: ShoppingCart, label: 'Sales Entry', path: '/sales-entry' },
        { icon: Receipt, label: 'Invoice Entry', path: '/purchases' },
        { icon: TrendingUp, label: 'Expenses', path: '/expenses' },
        { icon: Clock, label: 'Staff Hours', path: '/staff-hours' },
      ]
    },
    { 
      icon: Fuel, 
      label: 'Fuel', 
      isGroup: true,
      fuelOnly: true,
      children: [
        { icon: Fuel, label: 'Fuel Management', path: '/fuel' },
        { icon: CreditCard, label: 'Card Balances', path: '/fuel-card-balances' },
        { icon: AlertTriangle, label: 'Drive Off / Debt', path: '/drive-off-debt' },
      ]
    },
    { 
      icon: Coins, 
      label: 'Other Revenue', 
      isGroup: true,
      children: [
        { icon: Package, label: 'Scratch Cards', path: '/scratch-cards' },
        { icon: Ticket, label: 'Lottery', path: '/lottery' },
        { icon: Coins, label: 'Commission Income', path: '/commission-income' },
      ]
    },
    { 
      icon: Banknote, 
      label: 'Finance', 
      isGroup: true,
      children: [
        { icon: DollarSign, label: 'Cash Control', path: '/money-movement' },
        { icon: Landmark, label: 'Creditors', path: '/creditors' },
        { icon: Truck, label: 'Suppliers', path: '/suppliers' },
      ]
    },
    { icon: TrendingUp, label: 'Reports', path: '/reports' },
  ],
  staff: [
    { icon: ShoppingCart, label: 'Sales Entry', path: '/sales-entry' },
    { icon: Receipt, label: 'Invoice Entry', path: '/purchases' },
    { icon: TrendingUp, label: 'Expenses', path: '/expenses' },
    { icon: Fuel, label: 'Fuel Management', path: '/fuel', fuelOnly: true },
    { icon: AlertTriangle, label: 'Drive Off / Debt', path: '/drive-off-debt', fuelOnly: true },
    { icon: Landmark, label: 'Creditors', path: '/creditors' },
    { icon: Clock, label: 'Staff Hours', path: '/staff-hours' },
  ],
  creditor: [
    { icon: LayoutDashboard, label: 'My Dashboard', path: '/creditor-dashboard' },
    { icon: FileText, label: 'Submit Request', path: '/creditor-requests/new' },
    { icon: ClipboardList, label: 'Request History', path: '/creditor-requests/history' },
  ],
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { stores, selectedStore, setSelectedStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Theme state - check localStorage or default to dark
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return true; // Default to dark mode
  });

  // Apply theme on mount and when it changes
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Auto-expand group if current path is in it
  useEffect(() => {
    const userMenu = menuStructure[user?.user_type] || menuStructure.staff;
    userMenu.forEach(item => {
      if (item.isGroup && item.children) {
        const isChildActive = item.children.some(child => location.pathname === child.path);
        if (isChildActive) {
          setExpandedGroups(prev => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname, user?.user_type]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const userMenuItems = menuStructure[user?.user_type] || menuStructure.staff;

  // Check if user has access to fuel stations
  const hasFuelAccess = () => {
    if (user?.user_type === 'super_user') return true;
    const userStoreIds = user?.assigned_stores || [];
    const userStores = stores.filter(s => userStoreIds.includes(s.id));
    return userStores.some(s => s.store_type === 'fuel_station');
  };

  const handleLogout = () => {
    runNavigationGuard(async () => {
      await logout();
      navigate('/login');
    });
  };

  // Intercept sidebar links when the current page has unsaved data
  const guardedLinkClick = (e, path) => {
    setSidebarOpen(false);
    if (hasNavigationGuard() && path !== location.pathname) {
      e.preventDefault();
      runNavigationGuard(() => navigate(path));
    }
  };

  const handleStoreChange = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
    }
  };

  // Render a single menu item
  const renderMenuItem = (item, isChild = false) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.path.replace('/', '')}`}
        onClick={(e) => guardedLinkClick(e, item.path)}
        className={`
          flex items-center gap-3 px-3 py-2 rounded-lg
          transition-all duration-200
          ${isChild ? 'ml-4 text-sm' : ''}
          ${isActive 
            ? 'bg-primary text-primary-foreground primary-glow' 
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }
        `}
      >
        <item.icon className={`${isChild ? 'h-4 w-4' : 'h-5 w-5'}`} />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  // Render a group with children
  const renderGroup = (item) => {
    const isExpanded = expandedGroups[item.label];
    const hasActiveChild = item.children?.some(child => location.pathname === child.path);
    
    return (
      <div key={item.label} className="space-y-1">
        <button
          onClick={() => toggleGroup(item.label)}
          data-testid={`nav-group-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          className={`
            w-full flex items-center justify-between px-3 py-2 rounded-lg
            transition-all duration-200
            ${hasActiveChild 
              ? 'bg-primary/10 text-primary' 
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }
          `}
        >
          <div className="flex items-center gap-3">
            <item.icon className="h-5 w-5" />
            <span className="font-medium">{item.label}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        
        {isExpanded && (
          <div className="space-y-1 overflow-hidden animate-in slide-in-from-top-2 duration-200">
            {item.children
              .filter(child => !child.fuelOnly || hasFuelAccess())
              .map(child => renderMenuItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-card border-r border-border
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 border-b border-border relative">
            <Link to="/dashboard" className="flex items-center justify-center" onClick={(e) => guardedLinkClick(e, '/dashboard')}>
              <div className="p-2 rounded-lg bg-white/95">
                <img 
                  src="/promortek-logo.jpg" 
                  alt="PROMORTEK" 
                  className="h-9 w-auto"
                />
              </div>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden absolute right-4"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Store Selector */}
          {stores.length > 0 && user?.user_type !== 'super_user' && (
            <div className="px-4 py-3 border-b border-border">
              <Select value={selectedStore?.id} onValueChange={handleStoreChange}>
                <SelectTrigger className="w-full bg-muted border-0" data-testid="store-selector">
                  <SelectValue placeholder="Select Store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {userMenuItems
                .filter(item => {
                  // Filter out fuel-only items/groups if user has no access to fuel stations
                  if (item.fuelOnly && !hasFuelAccess()) return false;
                  return true;
                })
                .map((item) => {
                  if (item.isGroup) {
                    return renderGroup(item);
                  }
                  return renderMenuItem(item);
                })}
            </nav>
          </ScrollArea>

          {/* User info & Logout */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.user_type?.replace('_', ' ')}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex-1 lg:flex-none">
            {selectedStore && (
              <div className="hidden lg:flex items-center gap-2 text-muted-foreground">
                <Store className="h-4 w-4" />
                <span className="text-sm">{selectedStore.name}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Theme Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              data-testid="theme-toggle"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-yellow-500" />
              ) : (
                <Moon className="h-5 w-5 text-blue-500" />
              )}
            </Button>
            
            <span className="text-sm text-muted-foreground hidden sm:block">
              {new Date().toLocaleDateString('en-GB', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <div className="fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
